import { useMemo, useState } from "react";
import type { Moment } from "moment";
import { taskService } from "@miplatform/api";
import { DATE_TIME_FORMATS, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IAttemptListItemResponseForWorkOrder,
  ICalendarSet,
} from "@miplatform/types";
import { formatDate, getDate, isSame } from "@miplatform/utils";

export const NHC_ASSIGNMENT_BOARD_DAY_COUNT = 5;

const emptyStagingState: StagingState = {
  stagedByUserId: {},
  stagingStack: [],
};

const EMPTY_STAGED: StagedWorkOrderAssignment[] = [];
const DEFAULT_DAY_START_TIME = "08:00";

export function useAssignmentStaging(
  calendarSet?: ICalendarSet | null,
  assignmentDate?: Moment | null,
  calendarEvents: IAttemptListItemResponseForWorkOrder[] = []
) {
  const [stagingState, setStagingState] =
    useState<StagingState>(emptyStagingState);
  const [confirmedAttemptIds, setConfirmedAttemptIds] = useState<Set<string>>(
    () => new Set<string>()
  );
  const stateManager = useComponentState();

  const { stagedByUserId } = stagingState;
  const isConfirming = stateManager.isBusy();

  const stagingMeta = useMemo(() => {
    const ids = new Set<string>();
    let total = 0;
    for (const entries of Object.values(stagedByUserId)) {
      total += entries.length;
      for (const s of entries) {
        ids.add(s.attempt.id);
      }
    }
    return { ids, total };
  }, [stagedByUserId]);

  const stagedIds = stagingMeta.ids;
  const totalStagedCount = stagingMeta.total;

  const stageWorkOrderForUser = (
    attempt: IAttemptListItemResponseForWorkOrder,
    userId: string
  ) => {
    setStagingState((prev) => {
      if (hasStagedAttemptId(prev.stagedByUserId, attempt.id)) {
        return prev;
      }
      const existing = prev.stagedByUserId[userId] ?? [];

      const dayOffset = findDayOffsetForStagedAttempt(
        calendarSet,
        assignmentDate,
        userId,
        calendarEvents,
        existing,
        attempt,
        NHC_ASSIGNMENT_BOARD_DAY_COUNT
      );

      return {
        stagedByUserId: {
          ...prev.stagedByUserId,
          [userId]: [...existing, { attempt, dayOffset }],
        },
        stagingStack: [...prev.stagingStack, { userId, attemptId: attempt.id }],
      };
    });
  };

  const undoLastTransfer = () => {
    setStagingState((prev) => {
      if (!prev.stagingStack.length) {
        return prev;
      }
      const last = prev.stagingStack[prev.stagingStack.length - 1];
      return {
        stagingStack: prev.stagingStack.slice(0, -1),
        stagedByUserId: removeAttemptFromUserMap(
          prev.stagedByUserId,
          last.userId,
          last.attemptId
        ),
      };
    });
  };

  const clearStaging = () => {
    setStagingState(emptyStagingState);
  };

  const confirmStaging = async () => {
    if (stateManager.isBusy()) {
      return false;
    }

    const data = buildBulkAssignPayload({
      stagedByUserId,
      calendarSet,
      assignmentDate,
      calendarEvents,
    });

    if (!data.length) {
      return false;
    }

    try {
      const response = await stateManager.run(() =>
        taskService.attempts.bulkAssign({
          data,
          toastMessageConfig: undefined,
        })
      );
      if (response.status === HTTP_STATUSES.OK) {
        setConfirmedAttemptIds((prev) => {
          const next = new Set(prev);
          for (const payloadItem of data) {
            next.add(payloadItem.AttemptId);
          }
          return next;
        });
        clearStaging();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const clearStagingForUser = (userId: string) => {
    setStagingState((prev) => {
      const hasStagedForUser = Boolean(prev.stagedByUserId[userId]?.length);
      const hasStackForUser = prev.stagingStack.some(
        (entry) => entry.userId === userId
      );
      if (!hasStagedForUser && !hasStackForUser) {
        return prev;
      }
      const nextMap = hasStagedForUser
        ? { ...prev.stagedByUserId }
        : prev.stagedByUserId;
      if (hasStagedForUser) {
        delete nextMap[userId];
      }
      const nextStack = hasStackForUser
        ? prev.stagingStack.filter((entry) => entry.userId !== userId)
        : prev.stagingStack;
      return {
        stagedByUserId: nextMap,
        stagingStack: nextStack,
      };
    });
  };

  const unstageAttempt = (userId: string, attemptId: string) => {
    setStagingState((prev) => {
      const list = prev.stagedByUserId[userId];
      if (!list?.some((s) => s.attempt.id === attemptId)) {
        return prev;
      }
      const nextStack = prev.stagingStack.filter(
        (entry) => !(entry.userId === userId && entry.attemptId === attemptId)
      );
      return {
        stagingStack:
          nextStack.length === prev.stagingStack.length
            ? prev.stagingStack
            : nextStack,
        stagedByUserId: removeAttemptFromUserMap(
          prev.stagedByUserId,
          userId,
          attemptId
        ),
      };
    });
  };

  const getStagedForUser = (userId: string) =>
    stagedByUserId[userId] ?? EMPTY_STAGED;

  return {
    stagedByUserId,
    stagedIds,
    confirmedAttemptIds,
    totalStagedCount,
    isConfirming,
    stageWorkOrderForUser,
    undoLastTransfer,
    confirmStaging,
    clearStaging,
    clearStagingForUser,
    unstageAttempt,
    getStagedForUser,
  };
}

export type StagedWorkOrderAssignment = {
  attempt: IAttemptListItemResponseForWorkOrder;
  dayOffset: number;
};

type StagingStackEntry = {
  userId: string;
  attemptId: string;
};

type StagingState = {
  stagedByUserId: Record<string, StagedWorkOrderAssignment[]>;
  stagingStack: StagingStackEntry[];
};

function hasStagedAttemptId(
  stagedByUserId: Record<string, StagedWorkOrderAssignment[]>,
  attemptId: string
): boolean {
  for (const userId of Object.keys(stagedByUserId)) {
    const entries = stagedByUserId[userId];
    if (entries?.some((s) => s.attempt.id === attemptId)) {
      return true;
    }
  }
  return false;
}

function removeAttemptFromUserMap(
  prev: Record<string, StagedWorkOrderAssignment[]>,
  userId: string,
  attemptId: string
): Record<string, StagedWorkOrderAssignment[]> {
  const list = prev[userId];
  if (!list?.length) {
    return prev;
  }
  const nextList = list.filter((s) => s.attempt.id !== attemptId);
  if (nextList.length === list.length) {
    return prev;
  }
  const next = { ...prev };
  if (nextList.length === 0) {
    delete next[userId];
  } else {
    next[userId] = nextList;
  }
  return next;
}

function getExpectedDurationMinutes(
  attempt: IAttemptListItemResponseForWorkOrder
): number {
  return attempt.expectedDuration ?? 60;
}

function sumExpectedDurationMinutes(
  attempts: IAttemptListItemResponseForWorkOrder[]
): number {
  return attempts.reduce((sum, a) => sum + getExpectedDurationMinutes(a), 0);
}

function getCalendarDayWorkingMinutes(
  calendarSet: ICalendarSet | null | undefined,
  day: Moment
): number | null {
  const dayStartStr = calendarSet?.dayStartTime;
  const dayEndStr = calendarSet?.dayEndTime;
  if (!dayStartStr || !dayEndStr) {
    return null;
  }
  const dayKey = formatDate(day, DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD);
  const start = getDate(`${dayKey} ${dayStartStr}`);
  const end = getDate(`${dayKey} ${dayEndStr}`);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return Math.max(0, end.diff(start, "minutes"));
}

function calendarEventsForUserOnDay(
  events: IAttemptListItemResponseForWorkOrder[],
  userId: string,
  day: Moment
): IAttemptListItemResponseForWorkOrder[] {
  return events.filter((e) => {
    if (e.assignedUserId !== userId) {
      return false;
    }
    const sd = e.scheduledStartDate ?? e.startDate ?? e.scheduleDate;
    if (!sd) {
      return false;
    }
    return isSame({ date1: sd, date2: day.toDate(), timeUnit: "day" });
  });
}

type BulkAssignAttemptPayload = {
  AttemptId: string;
  UserId: string;
  ScheduledStartDate: string;
  ScheduledEndDate: string;
};

function buildBulkAssignPayload({
  stagedByUserId,
  calendarSet,
  assignmentDate,
  calendarEvents,
}: {
  stagedByUserId: Record<string, StagedWorkOrderAssignment[]>;
  calendarSet?: ICalendarSet | null;
  assignmentDate?: Moment | null;
  calendarEvents: IAttemptListItemResponseForWorkOrder[];
}): BulkAssignAttemptPayload[] {
  if (!assignmentDate) {
    return [];
  }

  const dayStartTime = calendarSet?.dayStartTime ?? DEFAULT_DAY_START_TIME;
  const startOfAssignmentDay = assignmentDate.clone().startOf("day");
  const payload: BulkAssignAttemptPayload[] = [];

  for (const [userId, entries] of Object.entries(stagedByUserId)) {
    if (entries.length) {
      const entriesByDayOffset = entries.reduce<
        Record<number, StagedWorkOrderAssignment[]>
      >((acc, stagedAssignment) => {
        const key = stagedAssignment.dayOffset;
        const list = acc[key] ?? [];
        acc[key] = [...list, stagedAssignment];
        return acc;
      }, {});

      for (const [dayOffsetKey, dayEntries] of Object.entries(
        entriesByDayOffset
      )) {
        const dayOffset = Number(dayOffsetKey);
        const day = startOfAssignmentDay.clone().add(dayOffset, "days");
        const dayKey = formatDate(day, DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD);
        let cursor = getDate(`${dayKey} ${dayStartTime}`);
        if (!cursor.isValid()) {
          cursor = day.clone();
        }

        const existingDayMinutes = sumExpectedDurationMinutes(
          calendarEventsForUserOnDay(calendarEvents, userId, day)
        );
        cursor = cursor.clone().add(existingDayMinutes, "minutes");

        for (const { attempt } of dayEntries) {
          const duration = getExpectedDurationMinutes(attempt);
          const start = cursor.clone();
          const end = start.clone().add(duration, "minutes");

          payload.push({
            AttemptId: attempt.id,
            UserId: userId,
            ScheduledStartDate: start.toISOString(),
            ScheduledEndDate: end.toISOString(),
          });
          cursor = end;
        }
      }
    }
  }

  return payload;
}

export function findDayOffsetForStagedAttempt(
  calendarSet: ICalendarSet | null | undefined,
  anchorDate: Moment | null | undefined,
  userId: string,
  calendarEvents: IAttemptListItemResponseForWorkOrder[],
  existingStagedForUser: StagedWorkOrderAssignment[],
  attempt: IAttemptListItemResponseForWorkOrder,
  maxDayOffsets: number = NHC_ASSIGNMENT_BOARD_DAY_COUNT
): number {
  if (!anchorDate || maxDayOffsets < 1) {
    return 0;
  }

  const newMinutes = getExpectedDurationMinutes(attempt);
  const start = anchorDate.clone().startOf("day");
  const lastOffset = maxDayOffsets - 1;

  for (let offset = 0; offset < maxDayOffsets; offset++) {
    const day = start.clone().add(offset, "days");
    const capacity = getCalendarDayWorkingMinutes(calendarSet, day);
    if (capacity == null) {
      return 0;
    }
    const fromCalendar = sumExpectedDurationMinutes(
      calendarEventsForUserOnDay(calendarEvents, userId, day)
    );
    const fromStaged = sumExpectedDurationMinutes(
      existingStagedForUser
        .filter((s) => s.dayOffset === offset)
        .map((s) => s.attempt)
    );
    if (fromCalendar + fromStaged + newMinutes <= capacity) {
      return offset;
    }
  }

  return lastOffset;
}
